import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ColorScheme = 'light' | 'dark' | 'system';
export type EffectiveColorScheme = 'light' | 'dark';

const STORAGE_KEY = 'apexBackofficeColorScheme';

@Injectable({
  providedIn: 'root',
})
export class AppearanceService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly colorSchemeSubject = new BehaviorSubject<ColorScheme>(this.readInitialColorScheme());
  private readonly mediaQuery = this.createMediaQuery();

  readonly colorScheme$ = this.colorSchemeSubject.asObservable();

  constructor() {
    this.applyColorScheme(this.currentColorScheme);
    this.mediaQuery?.addEventListener?.('change', () => {
      if (this.currentColorScheme === 'system') {
        this.applyColorScheme('system');
      }
    });
  }

  get currentColorScheme(): ColorScheme {
    return this.colorSchemeSubject.value;
  }

  get effectiveColorScheme(): EffectiveColorScheme {
    return this.resolveEffectiveColorScheme(this.currentColorScheme);
  }

  get hasStoredColorSchemePreference(): boolean {
    return this.readStoredColorScheme() !== null;
  }

  toggleColorScheme(): void {
    this.setColorScheme(this.effectiveColorScheme === 'dark' ? 'light' : 'dark');
  }

  setColorScheme(colorScheme: ColorScheme): void {
    this.colorSchemeSubject.next(colorScheme);
    this.applyColorScheme(colorScheme);

    if (isPlatformBrowser(this.platformId)) {
      try {
        window.localStorage.setItem(STORAGE_KEY, colorScheme);
      } catch {
        // Preference persistence is optional when browser storage is unavailable.
      }
    }
  }

  private applyColorScheme(colorScheme: ColorScheme): void {
    const effectiveColorScheme = this.resolveEffectiveColorScheme(colorScheme);
    const root = this.document.documentElement;
    root.setAttribute('data-theme', effectiveColorScheme);
    root.setAttribute('data-theme-preference', colorScheme);
    root.classList.toggle('theme-dark', effectiveColorScheme === 'dark');
    root.classList.toggle('theme-light', effectiveColorScheme === 'light');
    root.classList.toggle('dark', effectiveColorScheme === 'dark');
    root.style.setProperty('color-scheme', effectiveColorScheme);

    if (this.document.body) {
      this.document.body.setAttribute('data-theme', effectiveColorScheme);
      this.document.body.setAttribute('data-theme-preference', colorScheme);
      this.document.body.classList.toggle('theme-dark', effectiveColorScheme === 'dark');
      this.document.body.classList.toggle('theme-light', effectiveColorScheme === 'light');
      this.document.body.classList.toggle('dark', effectiveColorScheme === 'dark');
    }
  }

  private readInitialColorScheme(): ColorScheme {
    if (!isPlatformBrowser(this.platformId)) {
      return 'system';
    }

    return this.readStoredColorScheme() ?? 'system';
  }

  private readStoredColorScheme(): ColorScheme | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
    } catch {
      // Some privacy modes block storage. Keep the application default predictable.
    }

    return null;
  }

  private resolveEffectiveColorScheme(colorScheme: ColorScheme): EffectiveColorScheme {
    if (colorScheme !== 'system') {
      return colorScheme;
    }

    return this.mediaQuery?.matches ? 'dark' : 'light';
  }

  private createMediaQuery(): MediaQueryList | null {
    if (!isPlatformBrowser(this.platformId) || typeof window.matchMedia !== 'function') {
      return null;
    }

    return window.matchMedia('(prefers-color-scheme: dark)');
  }
}
